import "./Timeline.css";
import React, { useRef, useState, useEffect } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { useTranslation } from "react-i18next";

const events = [
  {
    dateKey: "timeline.ebay.date",
    logo: "/images/EBay_logo.svg",
    instituteKey: "timeline.ebay.name",
    color: "#F02D2D",
    captionKey: "timeline.ebay.role"
  },
  {
    dateKey: "timeline.walmart.date",
    logo: "/images/walmart_logo.png",
    instituteKey: "timeline.walmart.name",
    color: "#0f1f41",
    captionKey: "timeline.walmart.role"
  },
  {
    dateKey: "timeline.nyu.date",
    logo: "/images/NYU_Seal.svg",
    instituteKey: "timeline.nyu.name",
    color: "#530296",
    captionKey: "timeline.nyu.role"
  },
  {
    dateKey: "timeline.osu.date",
    logo: "/images/OSU_Seal.svg",
    instituteKey: "timeline.osu.name",
    color: "#960202",
    captionKey: "timeline.osu.role"
  },
];

function Timeline() {
  const { t } = useTranslation("home");

  const containerRef = useRef(null);
  const axisRef = useRef(null);
  const eventRefs = useRef([]);

  const inView = useInView(containerRef, { once: true });
  const controls = useAnimation();
  const axisControls = useAnimation();

  const N = events.length;

  // Store ratio instead of pixels
  const [positions, setPositions] = useState([]);
  const [axisHeight, setAxisHeight] = useState(0);

  const [zIndices, setZIndices] = useState(events.map((_, i) => N - i));
  const [draggingIndex, setDraggingIndex] = useState(null);

  /*
    Initialize evenly spaced ratios (0 → 1)
  */
  useEffect(() => {
    const initial = events.map((_, i) =>
      N > 1 ? i / (N - 1) : 0.5
    );
    setPositions(initial);
  }, [N]);

  /*
    Observe axis height changes (rotation-safe)
  */
  useEffect(() => {
    if (!axisRef.current) return;

    const updateHeight = () => {
      const rect = axisRef.current.getBoundingClientRect();
      setAxisHeight(rect.height);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(axisRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  /*
    Drag logic (convert pixel movement → ratio)
  */
  const handlePointerDown = (index, e) => {
    e.preventDefault();
    setDraggingIndex(index);

    const startY = e.clientY;
    const startRatio = positions[index];

    setZIndices((prev) => {
      const copy = [...prev];
      copy[index] = 9999;
      return copy;
    });

    const onPointerMove = (moveEvent) => {
      const deltaPixels = moveEvent.clientY - startY;
      const deltaRatio = deltaPixels / axisHeight;

      let newRatio = startRatio + deltaRatio;

      // Clamp between 0 and 1
      newRatio = Math.max(0, Math.min(1, newRatio));

      setPositions((prev) => {
        const copy = [...prev];
        copy[index] = newRatio;
        return copy;
      });
    };

    const onPointerUp = () => {
      setZIndices(events.map((_, i) => N - i));
      setDraggingIndex(null);

      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  useEffect(() => {
    if (inView) {
      controls.start("visible");
      axisControls.start("visible");
    }
  }, [inView, controls, axisControls]);

  return (
    <div id="timeline-container" ref={containerRef}>
      <div className="axis-wrapper" ref={axisRef}>

        <motion.span
          className="axis-line-global"
          initial={{ opacity: 0, height: 0 }}
          animate={axisControls}
          variants={{
            hidden: { opacity: 0, height: 0 },
            visible: { opacity: 1, height: "100%" },
          }}
          transition={{ duration: 1, delay: 0.2 }}
        />

        {events.map((event, index) => {
          const isDragging = draggingIndex === index;

          // Convert ratio → pixels every render
          const pixelCenter = positions[index] * axisHeight;

          return (
            <div
              key={index}
              style={{
                position: "absolute",
                top:
                  pixelCenter -
                  (eventRefs.current[index]?.offsetHeight || 0) / 2,
                zIndex: zIndices[index],
                opacity: isDragging ? 0.8 : 1,
                width: "100%",
              }}
            >
              <motion.div
                className="draggable-event-row"
                ref={(el) => (eventRefs.current[index] = el)}
                initial={{ opacity: 0, y: -10 }}
                animate={controls}
                variants={{
                  hidden: { opacity: 0, y: -10 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{
                  duration: 0.8,
                  delay: 0.3 + index * 0.2,
                }}
                onPointerDown={(e) => handlePointerDown(index, e)}
              >
                <div className="axis-col">
                  <motion.span
                    className="logo"
                    animate={{
                      boxShadow: isDragging
                        ? "rgba(0,0,0,0.08) 0px 2px 8px"
                        : "rgba(100,100,111,0.2) 0px 7px 29px",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <img src={event.logo} alt={`${t(event.instituteKey)} logo`} />
                  </motion.span>
                </div>

                <div className="content-col">
                  <span className="connector" />
                  <motion.div
                    className="txt-box"
                    animate={{
                      scale: isDragging ? 1.08 : 1,
                      boxShadow: isDragging
                        ? "rgba(0,0,0,0.08) 0px 2px 8px"
                        : "rgba(100,100,111,0.2) 0px 7px 29px",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className="institute" style={{ color: event.color }}>
                      {t(event.instituteKey)}
                    </p>

                    <p className="date-inline">
                      {t(event.dateKey)}
                    </p>

                    <p className="caption">
                      {t(event.captionKey)}
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Timeline;